/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type { UnityRootState } from "../UnityAppStore";
import type { OsdState, ShowOsdPayload, TDisplacedOsd } from "./osd.types";
import { INITIAL_STATE } from "./osd.types";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "osdSlice";

const osdSlice = createSlice({
  name: "osd",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Show the OSD with the given message/code/priority.
     *
     * Only overwrites the currently displayed message when nothing is
     * outstanding, or when the incoming priority is greater than or equal to
     * the outstanding priority — mirrors kaveri's priority-guarded
     * `showErrorOSD` so a lower-priority request can never interrupt a
     * higher-priority message.
     *
     * "Outstanding" covers suspended as well as visible: a message parked by
     * `suspendOsd()` is still live and still owns its priority, so a
     * lower-priority request must not displace it just because it is
     * momentarily off screen. (Kaveri gates on visibility alone and therefore
     * lets a low-priority message clobber whatever its overlay had parked.)
     */
    showOsd(state: OsdState, action: PayloadAction<ShowOsdPayload>): void {
      const isOutstanding: boolean =
        state.isVisible === true || state.isSuspended === true;
      if (
        isOutstanding === false ||
        action.payload.priority >= state.priority
      ) {
        const easCountry: string = action.payload.easCountry || "";
        // An EAS alert taking the slot from an ordinary message: copy that
        // message aside so `hideEasOsd()` can put it back. Only for the alert
        // that displaces real content — an EAS alert replacing another must
        // not overwrite the stash with the alert it is replacing.
        if (easCountry.length > 0 && state.easCountry === "" && isOutstanding) {
          state.displacedOsd = {
            message: state.message,
            code: state.code,
            priority: state.priority,
            isRepositionRequired: state.isRepositionRequired,
            isSuspended: state.isSuspended
          };
        }

        state.isVisible = true;
        state.message = action.payload.message;
        state.code = action.payload.code;
        state.priority = action.payload.priority;
        state.isRepositionRequired = action.payload.isRepositionRequired;
        state.easCountry = easCountry;
        state.easHasVideo = action.payload.easHasVideo === true;
        state.easIsDismissible = action.payload.easIsDismissible === true;
        // A fresh message supersedes anything that was suspended — there is no
        // longer an older message worth restoring.
        state.isSuspended = false;
      }
    },

    /**
     * Hide the OSD — a permanent dismissal.
     *
     * Resets `priority` to 0 and clears `isSuspended`, so nothing is left
     * outstanding and a subsequent `showOsd()` of any priority is free to show.
     * `message`/`code` are left in place so exit animations can reference them.
     *
     * Contrast `suspendOsd()`, which keeps the message outstanding.
     */
    hideOsd(state: OsdState): void {
      state.isVisible = false;
      state.priority = 0;
      state.isSuspended = false;
    },

    /**
     * Dismiss an EAS alert, restoring whatever message it displaced.
     *
     * Used instead of `hideOsd()` by `EamBridge`, because an EAS alert is the
     * one producer that takes the slot from another message rather than
     * winning it outright. With nothing displaced this behaves exactly like
     * `hideOsd()`.
     *
     * A displaced message returns to the state it was in, suspended included:
     * one that was parked behind a dialog comes back parked, so the dialog is
     * not left with a message competing underneath it.
     */
    hideEasOsd(state: OsdState): void {
      const displaced: TDisplacedOsd | null = state.displacedOsd;
      state.easCountry = "";
      state.easHasVideo = false;
      state.easIsDismissible = false;
      state.displacedOsd = null;

      if (displaced === null) {
        state.isVisible = false;
        state.priority = 0;
        state.isSuspended = false;
        return;
      }

      state.message = displaced.message;
      state.code = displaced.code;
      state.priority = displaced.priority;
      state.isRepositionRequired = displaced.isRepositionRequired;
      state.isSuspended = displaced.isSuspended;
      state.isVisible = !displaced.isSuspended;
    },

    /**
     * Temporarily hide the OSD while retaining everything needed to bring it
     * back unchanged.
     *
     * Unlike `hideOsd()`, `priority` is deliberately NOT reset — the whole
     * point is that `resumeOsd()` can restore the same message at the same
     * priority once whatever took the screen (the full-screen dialog) goes
     * away. Kaveri achieves this by having its single `hideErrorOSD` action
     * inspect the overlay slice's state and conditionally skip the reset; an
     * explicit second action states the intent instead of inferring it.
     *
     * A no-op when the OSD is not currently visible, so a stray suspend cannot
     * mark an already-dismissed OSD as restorable.
     */
    suspendOsd(state: OsdState): void {
      if (state.isVisible === false) {
        return;
      }
      state.isVisible = false;
      state.isSuspended = true;
    },

    /**
     * Bring back an OSD previously hidden by `suspendOsd()`.
     *
     * Only acts when there is genuinely something to restore — the OSD must be
     * suspended and still carry a non-zero priority. This means a `hideOsd()`
     * that lands between suspend and resume permanently wins, which is the
     * intended precedence: an explicit dismissal outranks a restore.
     */
    resumeOsd(state: OsdState): void {
      if (state.isSuspended === false || state.priority === 0) {
        return;
      }
      state.isVisible = true;
      state.isSuspended = false;
    },

    /**
     * Reset the OSD state back to its initial values.
     */
    resetOsd(): OsdState {
      return INITIAL_STATE;
    }
  }
});

/* ── Actions ──────────────────────────────────────────────────────────── */
export const { showOsd, hideOsd, hideEasOsd, suspendOsd, resumeOsd, resetOsd } =
  osdSlice.actions;

/* ── Reducer ──────────────────────────────────────────────────────────── */
export const osdReducer = osdSlice.reducer;

/* ── Selectors ────────────────────────────────────────────────────────── */
export const selectIsOsdVisible = (state: UnityRootState): boolean =>
  state.osd.isVisible;

export const selectOsdMessage = (state: UnityRootState): string =>
  state.osd.message;

export const selectOsdCode = (state: UnityRootState): string => state.osd.code;

export const selectOsdPriority = (state: UnityRootState): number =>
  state.osd.priority;

export const selectOsdRepositionRequired = (state: UnityRootState): boolean =>
  state.osd.isRepositionRequired;

export const selectIsOsdSuspended = (state: UnityRootState): boolean =>
  state.osd.isSuspended;

export const selectOsdEasCountry = (state: UnityRootState): string =>
  state.osd.easCountry;

/**
 * Whether an EAS alert carrying video is on screen right now.
 *
 * Both halves are required: `easHasVideo` outlives its alert because
 * `hideOsd()` deliberately leaves the EAS fields in place for exit
 * animations, so visibility is what makes this true only while the alert is
 * actually up.
 */
export const selectIsEasVideoVisible = (state: UnityRootState): boolean =>
  state.osd.isVisible && state.osd.easHasVideo;

/**
 * Whether the EAS alert on screen may be dismissed by the viewer.
 *
 * The resolved decision from `EasDismissPolicy` — market template *and* the
 * alert's own `allowUserExit` — which is also exactly what `EasInputGate`
 * gates the dismiss key on. The footer renders on this and nothing else, so
 * what the OSD tells the viewer and what the key does cannot drift apart.
 *
 * @param state - The root state
 * @returns True when the alert permits manual dismissal
 */
export const selectOsdEasIsDismissible = (state: UnityRootState): boolean =>
  state.osd.easIsDismissible;
